import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const ScheduledReportView = ({
  serviceName,
  startDate,
  endDate,
  viewBy,
  connectedProfileId,
  reportId,
  subReportId = -1,
  currReportData,
    isCustomReport,
}) => {
  const { t: translate } = useTranslation();

  const sideBarWidth = 500;
  const { showToast } = useToaster();
  const { handleCloseRightSidebar } = useRightSidebar();
  let currentBrandId = CommonHelper.GetCurrentBrandId();
  const cuurentBrandTime = CommonHelper.getBrandTimeZoneTime();
  const brandDetails = CommonHelper.GetBrandInfoFromLocalStorage('brand_details');
  const currentBrandDetails = brandDetails?.find((a) => a.brand_id === currentBrandId);
  const [email, setEmail] = useState('');
   const [subject, setSubject] = useState(
    `${translate('report.check_out_the')}  ${translate('report.account')} ${
     isCustomReport
          ? currReportData.custom_report_name
          : currReportData.report_display_name
          ? currReportData.report_display_name
          : currReportData.card_name} ${translate('report.for')} ${currentBrandDetails.brand_name}`
  );
  const [message, setMessage] = useState(
    `${translate('report.just_wanted_to_share')} ${
     isCustomReport
          ? currReportData.custom_report_name
          : currReportData.report_display_name
          ? currReportData.report_display_name
          : currReportData.card_name} 
     ${translate('report.report_for')} ${currentBrandDetails.brand_name} ${translate('report.with_you')}. ${translate(
      'report.see_attachment_below'
    )}.`
  );
  const [format, setFormat] = useState(translate('report.pdf'));
  const [passwordProtect, setPasswordProtect] = useState(false);
  const [dateRange, setDateRange] = useState(translate('report.last_7_days'));
  const [groupBy, setGroupBy] = useState(translate('report.day'));
  const [frequency, setFrequency] = useState(translate('report.daily'));
  const [sendTime, setSendTime] = useState(cuurentBrandTime);
  const [height, setHeight] = useState(window.innerHeight - 150);
  const [isLoading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const is24HourFormat = CommonHelper.GetBrandInfoFromLocalStorage('is_24_hour_format');
  const [selectedDays, setSelectedDays] = useState({
    weekly_days: [0],
    monthly_days: [1],
  });

  const handleSend = () => {
    // Reset error messages
    setEmailError('');

    if (!email) {
      setEmailError(translate('report.email_address_is_required'));
      showToast(translate('report.email_address_is_required'), 'e');
      return;
    }

    // Validate email format
    const emailList = email.split(',').map((e) => e.trim());
    const isValidEmail = emailList.every((emailItem) => /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(emailItem));

    if (!isValidEmail) {
      setEmailError(translate('report.please_enter_valid_email'));
      showToast(translate('report.please_enter_valid_email'), 'e');
      return;
    }

    if (!subject || subject.trim() === '') {
      showToast(translate('report.subject_is_requuired'), 'e');
      return;
    }

    if (!message || message.trim() === '') {
      showToast(translate('report.message_is_required'), 'e');
      return;
    }

    setLoading(true);
    const formBody = {
      report_id: reportId,
      sub_report_id: subReportId,
      service_name: serviceName,
      connected_profile_id: connectedProfileId,
      recipient_email_addresses: emailList,
      subject: subject,
      email_body: message,
      date_range: dateRange,
      group_by: viewBy,
      schedule_type: frequency,
      scheduled_time: sendTime,
      is_custom_report : isCustomReport,
      report_format: format,
    };

    if (frequency === 'weekly') {
      formBody.schedule_type = 'weekly';
      formBody.weekly_days = selectedDays.weekly_days;
    } else if (frequency === 'monthly') {
      formBody.schedule_type = 'monthly';
      formBody.monthly_days = selectedDays.monthly_days;
    }

    const getProps = {
      url: reportModuleURL.addScheduledReport,
      body: formBody,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(getProps);
  };

  const handleDayChange = (event, type) => {
    const value = event.target.value;
    setSelectedDays((prevSelectedDays) => {
      const updatedDays = { ...prevSelectedDays };

      if (type === 'weekly') {
        updatedDays.weekly_days = value;
      } else if (type === 'monthly') {
        updatedDays.monthly_days = value;
      }

      return updatedDays;
    });
  };
  const successCallback = (data, message) => {
    showToast(message, 's');
    setLoading(false);
    handleCloseRightSidebar();
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
  };

  const handleScheduleTime = (newTime) => {
    if (newTime) {
      // Convert the new time to a specific timezone and ensure it's a dayjs object
      const convertedTime = CommonHelper.getBrandTimeZoneTime(newTime);
      setSendTime(convertedTime);
    }
  };

  return (
    <StyledComponent.StyledStack sx={{ width: sideBarWidth, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {translate('scheduled_reports.scheduled_report')}
      </Typography>
      <Stack spacing={2}>
        <TextField
          label={translate('report.recipient_email_address')}
          placeholder={translate('report.email_seperated_by_comma')}
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
        />

        <TextField
          label={translate('report.subject')}
          multiline
          rows={2}
          fullWidth
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextField
          label={translate('report.message')}
          multiline
          rows={5}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Box>
          <Typography variant="body1">{translate('scheduled_reports.date_range')}</Typography>
          <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)} fullWidth>
            <MenuItem value="last_7_days">{translate('scheduled_reports.last_7_days')}</MenuItem>
            <MenuItem value="last_30_days">{translate('scheduled_reports.last_30_days')}</MenuItem>
            <MenuItem value="last_90_days">{translate('scheduled_reports.last_90_days')}</MenuItem>
          </Select>
        </Box>

        {/* Group By Field */}
        <Box>
          <Typography variant="body1">{translate('scheduled_reports.group_data_by')}</Typography>
          <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} fullWidth>
            <MenuItem value="day">{translate('scheduled_reports.day')}</MenuItem>
            <MenuItem value="week">{translate('scheduled_reports.week')}</MenuItem>
            <MenuItem value="month">{translate('calendar.month')}</MenuItem>
          </Select>
        </Box>

        {/* Frequency Field */}
        <Box>
          <Typography variant="body1">{translate('scheduled_reports.often_should_we_send_it')}</Typography>
          <Select value={frequency} onChange={(e) => setFrequency(e.target.value)} fullWidth>
            <MenuItem value="daily">{translate('scheduled_reports.daily')}</MenuItem>
            <MenuItem value="weekly">{translate('scheduled_reports.weekly')}</MenuItem>
            <MenuItem value="monthly">{translate('scheduled_reports.monthly')}</MenuItem>
          </Select>
        </Box>
        {/* Conditional rendering of day checkboxes for Week and Month */}
        {/* Conditional rendering of day checkboxes for Week and Month */}
        {(frequency === 'weekly' || frequency === 'monthly') && (
          <Box>
            <Typography variant="body1">
              {frequency === 'weekly'
                ? `${translate('scheduled_reports.select_days_of_the_week')}`
                : `${translate('scheduled_reports.select_days_of_the_month')}`}
            </Typography>

            {/* Weekly Days Selection */}
            {frequency === 'weekly' && (
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedDays.weekly_days}
                  onChange={(e) => handleDayChange(e, 'weekly')}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={1}>
                      {selected.map((day) => (
                        <Chip
                          key={day}
                          label={
                            [
                              `${translate('scheduled_reports.sunday')}`,
                              `${translate('scheduled_reports.monday')}`,
                              `${translate('scheduled_reports.tuesday')}`,
                              `${translate('scheduled_reports.wednesday')}`,
                              `${translate('scheduled_reports.thursday')}`,
                              `${translate('scheduled_reports.friday')}`,
                              `${translate('scheduled_reports.saturday')}`,
                            ][day]
                          }
                        />
                      ))}
                    </Stack>
                  )}
                >
                  {[
                    `${translate('scheduled_reports.sunday')}`,
                    `${translate('scheduled_reports.monday')}`,
                    `${translate('scheduled_reports.tuesday')}`,
                    `${translate('scheduled_reports.wednesday')}`,
                    `${translate('scheduled_reports.thursday')}`,
                    `${translate('scheduled_reports.friday')}`,
                    `${translate('scheduled_reports.saturday')}`,
                  ].map((day, i) => (
                    <MenuItem key={day} value={i}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Monthly Days Selection */}
            {frequency === 'monthly' && (
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedDays.monthly_days}
                  onChange={(e) => handleDayChange(e, 'monthly')}
                  renderValue={(selected) => (
                    <Stack direction="row" spacing={1}>
                      {selected.map((day) => (
                        <Chip key={day} label={`${translate('scheduled_reports.day')} ${day}`} />
                      ))}
                    </Stack>
                  )}
                >
                  {[...Array(31).keys()].map((day) => (
                    <MenuItem key={day} value={day + 1}>
                      {`${translate('scheduled_reports.day')} ${day + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Send Time Field */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Typography variant="body1">{translate('scheduled_reports.choose_send_time')}</Typography>
          <TimePicker
            value={dayjs(sendTime)} // Pass the dayjs object directly
            onChange={handleScheduleTime}
            ampm={!is24HourFormat}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>

        <Box>
          <Typography variant="body1" gutterBottom>
            {translate('report.report_format')}
          </Typography>
          <Select value={format} onChange={(e) => setFormat(e.target.value)} fullWidth>
            <MenuItem value="PDF">{translate('report.pdf')}</MenuItem>
            {/* <MenuItem value="PNG" disabled>PNG</MenuItem> */}
          </Select>
        </Box>

        <Divider />

        <Stack direction="row" spacing={2}>
          <LoadingButton variant="contained" color="primary" onClick={handleSend} loading={isLoading}>
            {translate('report.send')}
          </LoadingButton>
          {/* <Button color="inherit" onClick={() => console.log('Cancel clicked')}>Cancel</Button> */}
        </Stack>
      </Stack>
    </StyledComponent.StyledStack>
  );
};

export default ScheduledReportView;
